/**
 * PureEatiz · product page behaviour (sections/pe-main-product.liquid)
 *
 * Add to cart for the main buy box is handled entirely by Horizon's own
 * <product-form-component> / <add-to-cart-component> / <variant-picker> elements.
 * This module adds the PureEatiz extras and a small `addToCart()` helper for
 * secondary buttons ("+ ADD" on the coffee shelf). The helper mirrors
 * product-form.js: it dispatches Shopify's standard `CartLinesUpdateEvent`
 * (from '@shopify/events') with a deferred promise, POSTs to /cart/add.js with
 * the cart drawer's section ids, then resolves the promise with the fresh cart +
 * rendered sections. Horizon's <cart-drawer-component> (auto-open),
 * <cart-items-component> (drawer contents) and <cart-icon> (bubble) all listen
 * to that event, so the result is identical to a native Horizon add.
 */
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const ADDED_RESET_MS = 1600;

/**
 * @param {string} base - routes.cart_add_url (without .js)
 */
function cartAddUrl(base) {
  return `${base || '/cart/add'}.js`;
}

async function fetchCart() {
  const root = window.Shopify?.routes?.root || '/';
  const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
  return res.json();
}

/**
 * Adds lines to the cart and lets Horizon's cart drawer + bubble react, exactly like product-form.js.
 * @param {{id: string|number, quantity: number}[]} items
 * @param {Element} [source] - Element the event is dispatched from (bubbles to document)
 * @param {string} [addUrlBase]
 * @returns {Promise<any>} resolves with the /cart/add.js response, rejects with an Error on failure
 */
export async function addToCart(items, source = document.body, addUrlBase = '/cart/add') {
  const sectionIds = [
    ...new Set(
      Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
        .filter(Boolean)
    ),
  ];
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  let response;
  try {
    const res = await fetch(cartAddUrl(addUrlBase), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    response = await res.json();
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
    );
    throw error;
  }

  const didError = Boolean(response?.status);

  try {
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'pe-product',
        itemCount,
        sections: didError ? undefined : response.sections,
        didError,
      },
    });
  } catch (error) {
    deferred.reject(error);
  }

  if (didError) {
    source.dispatchEvent(
      new CartErrorEvent({
        error: response.message || 'Add to cart failed',
        code: 'INVALID',
        detail: { description: response.description, errors: response.errors },
      })
    );
    throw new Error(response.description || response.message || 'Add to cart failed');
  }

  return response;
}

// Small public hook so other PureEatiz sections can reuse the same integration.
window.PureEatiz = Object.assign(window.PureEatiz || {}, { addToCart });

class PeProductInfo extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {IntersectionObserver | undefined} */
  #stickyObserver;
  /** @type {MutationObserver | undefined} */
  #atcObserver;
  /** @type {number} */
  #activeIndex = 0;
  /** @type {number} */
  #scrollFrame = 0;
  /** @type {number} */
  #scrollLockUntil = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const section = this.closest('.shopify-section') || this;

    this.#initGallery(signal);
    this.#initSticky(signal);
    this.#initPincode(signal);

    section.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idle(() => this.#loadRecommendations());
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#stickyObserver?.disconnect();
    this.#atcObserver?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
  }

  /* ---------------------------------------------------------------- helpers */

  /** @param {number} cents */
  money(cents) {
    const formatted = formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'INR');
    return formatted.replace(/([.,])00(?!\d)/, '');
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pe-track]'));
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-pe-slide]')));
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-type="add-to-cart-form"]'));
  }

  get #mainAtc() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pe-main-atc]'));
  }

  get #qtyInput() {
    return /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="quantity"]') ?? null);
  }

  /* ---------------------------------------------------------------- gallery */

  /** @param {AbortSignal} signal */
  #initGallery(signal) {
    const track = this.#track;
    if (!track) return;

    const initial = this.querySelector('.pe-mp__thumb[aria-current="true"], .pe-mp__dot[aria-current="true"]');
    const initialIndex = initial instanceof HTMLElement ? Number(initial.dataset.peThumb) || 0 : 0;
    if (initialIndex > 0) this.goTo(initialIndex, false);

    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#scrollFrame);
        this.#scrollFrame = requestAnimationFrame(() => {
          if (Date.now() < this.#scrollLockUntil) return;
          const width = track.clientWidth || 1;
          const index = Math.round(track.scrollLeft / width);
          if (index !== this.#activeIndex) this.#setActive(index);
        });
      },
      { passive: true, signal }
    );

    track.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.goTo(this.#activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      },
      { signal }
    );
  }

  /**
   * @param {number} index
   * @param {boolean} [smooth]
   */
  goTo(index, smooth = true) {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || !slides.length) return;
    const clamped = Math.max(0, Math.min(slides.length - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (smooth && !reduce) this.#scrollLockUntil = Date.now() + 600;
    track.scrollTo({ left: clamped * track.clientWidth, behavior: smooth && !reduce ? 'smooth' : 'instant' });
    this.#setActive(clamped);
  }

  /** @param {string|number} mediaId */
  goToMedia(mediaId) {
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index > -1) this.goTo(index);
  }

  /** @param {number} index */
  #setActive(index) {
    this.#activeIndex = index;
    this.querySelectorAll('[data-pe-thumb]').forEach((thumb) => {
      if (!(thumb instanceof HTMLElement)) return;
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.peThumb) === index));
    });
    this.#slides.forEach((slide, i) => {
      if (i === index) return;
      slide.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  /* ---------------------------------------------------------------- variant change */

  /** @param {Event & {promise?: Promise<any>, target: EventTarget | null}} event */
  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (html) {
          this.querySelectorAll('[data-pe-sync][id]').forEach((el) => {
            const fresh = html.querySelector(`#${CSS.escape(el.id)}`);
            if (fresh) el.replaceWith(document.importNode(fresh, true));
          });
        }
        const mediaId = detail?.resource?.featured_media?.id;
        if (mediaId) this.goToMedia(mediaId);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pe-product] variant update failed', error);
      });
  };

  /* ---------------------------------------------------------------- free shipping */

  /** @param {Event & {promise?: Promise<any>}} event */
  #onCartUpdate = (event) => {
    const meter = this.querySelector('[data-pe-ship]');
    if (!meter) return;
    event.promise
      ?.then(() => fetchCart())
      .then((cart) => this.#renderShipping(cart.total_price))
      .catch(() => {});
  };

  /** @param {number} total */
  #renderShipping(total) {
    const meter = /** @type {HTMLElement | null} */ (this.querySelector('[data-pe-ship]'));
    const lead = /** @type {HTMLElement | null} */ (this.querySelector('[data-pe-ship-lead]'));
    if (!meter || !lead) return;
    const remaining = Number(meter.dataset.threshold || 0) - Number(total || 0);
    meter.classList.toggle('is-unlocked', remaining <= 0);
    lead.textContent =
      remaining > 0 ? (lead.dataset.template || '').replace('[amount]', this.money(remaining)) : lead.dataset.success || '';
  }

  /* ---------------------------------------------------------------- clicks */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const add = target.closest('[data-pe-add]');
    if (add instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#addShelfItem(add);
      return;
    }

    const buyNow = target.closest('[data-pe-buy-now]');
    if (buyNow instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#buyNow(buyNow);
      return;
    }

    const thumb = target.closest('[data-pe-thumb]');
    if (thumb instanceof HTMLElement) {
      this.goTo(Number(thumb.dataset.peThumb) || 0);
      return;
    }

    const copy = target.closest('[data-pe-copy]');
    if (copy instanceof HTMLElement) {
      this.#copyCode(copy);
      return;
    }

    const step = target.closest('[data-pe-sticky-step]');
    if (step instanceof HTMLElement) {
      this.#stepSticky(Number(step.dataset.peStickyStep) || 0);
      return;
    }

    if (target.closest('[data-pe-sticky-atc]')) {
      const main = this.#mainAtc;
      if (main && !main.disabled) main.click();
    }
  };

  /** @param {HTMLButtonElement} button */
  async #addShelfItem(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const label = button.querySelector('span');
    const original = label?.textContent ?? '';
    button.setAttribute('aria-busy', 'true');
    try {
      await addToCart([{ id: button.dataset.peAdd || '', quantity: 1 }], button, this.dataset.cartAddUrl);
      if (label) label.textContent = (typeof Theme !== 'undefined' && Theme.translations?.added) || 'Added';
    } catch (error) {
      if (label) label.textContent = '!';
      button.title = error instanceof Error ? error.message : '';
    } finally {
      button.removeAttribute('aria-busy');
      setTimeout(() => {
        if (label) label.textContent = original;
        button.removeAttribute('title');
      }, ADDED_RESET_MS);
    }
  }

  /** @param {HTMLButtonElement} button */
  async #buyNow(button) {
    const form = this.#form;
    const id = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value;
    if (!form || !id || button.disabled) return;
    const quantity = Math.max(1, Number(this.#qtyInput?.value) || 1);
    button.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(cartAddUrl(this.dataset.cartAddUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
    } catch (error) {
      button.removeAttribute('aria-busy');
      const errorEl = this.querySelector('.pe-mp__error');
      if (errorEl) {
        errorEl.classList.remove('hidden');
        const text = errorEl.querySelector('[data-pe-error-text]') || errorEl.appendChild(document.createElement('span'));
        text.setAttribute('data-pe-error-text', '');
        text.textContent = error instanceof Error ? error.message : 'Something went wrong';
        setTimeout(() => errorEl.classList.add('hidden'), 8000);
      }
    }
  }

  /** @param {HTMLElement} button */
  async #copyCode(button) {
    const code = button.dataset.peCopy || '';
    const text = button.querySelector('[data-pe-copy-text]');
    try {
      await navigator.clipboard.writeText(code);
      if (text) {
        text.textContent = button.dataset.copied || 'Copied!';
        setTimeout(() => (text.textContent = code), ADDED_RESET_MS);
      }
    } catch {
      /* clipboard unavailable – code stays visible to copy manually */
    }
  }

  /* ---------------------------------------------------------------- pincode */

  /** @param {AbortSignal} signal */
  #initPincode(signal) {
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('[data-pe-pincode]'));
    if (!form) return;
    const input = /** @type {HTMLInputElement} */ (form.elements.namedItem('pincode'));
    const message = /** @type {HTMLElement | null} */ (this.querySelector('.pe-mp__pin-msg'));

    try {
      const saved = localStorage.getItem('pe-pincode');
      if (saved && input) input.value = saved;
    } catch {
      /* storage unavailable */
    }

    input?.addEventListener(
      'input',
      () => {
        const digits = input.value.replace(/\D/g, '').slice(0, 6);
        if (digits !== input.value) input.value = digits;
        input.removeAttribute('aria-invalid');
      },
      { signal }
    );

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        if (!input || !message) return;
        const value = input.value.trim();
        const valid = /^[1-9][0-9]{5}$/.test(value);
        if (valid) input.removeAttribute('aria-invalid');
        else input.setAttribute('aria-invalid', 'true');
        message.classList.toggle('is-error', !valid);
        message.textContent = valid
          ? (form.dataset.success || '').replace(/\[pincode\]/g, value)
          : form.dataset.error || '';
        message.hidden = false;
        if (valid) {
          try {
            localStorage.setItem('pe-pincode', value);
          } catch {
            /* storage unavailable */
          }
        }
      },
      { signal }
    );
  }

  /* ---------------------------------------------------------------- sticky bar (mobile) */

  /** @param {AbortSignal} _signal */
  #initSticky(_signal) {
    const sticky = /** @type {HTMLElement | null} */ (this.querySelector('[data-pe-sticky]'));
    const actions = this.querySelector('[data-pe-actions]');
    if (!sticky || !actions) return;

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const show = !entry.isIntersecting;
        sticky.classList.toggle('is-visible', show);
        sticky.inert = !show;
        if (show) this.#syncStickyValue();
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(actions);

    const syncButton = () => {
      const main = this.#mainAtc;
      const stickyBtn = /** @type {HTMLButtonElement | null} */ (sticky.querySelector('[data-pe-sticky-atc]'));
      const label = sticky.querySelector('[data-pe-sticky-label]');
      if (!main || !stickyBtn) return;
      stickyBtn.disabled = main.disabled;
      const mainLabel = main.querySelector('.pe-mp__atc-label')?.textContent?.trim();
      if (label && mainLabel) label.textContent = mainLabel;
    };
    const container = this.querySelector('add-to-cart-component');
    if (container) {
      this.#atcObserver = new MutationObserver(syncButton);
      this.#atcObserver.observe(container, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
    }
  }

  #syncStickyValue() {
    const output = this.querySelector('[data-pe-sticky-value]');
    const input = this.#qtyInput;
    if (output && input) output.textContent = input.value || input.min || '1';
  }

  /** @param {number} direction */
  #stepSticky(direction) {
    const input = this.#qtyInput;
    const output = this.querySelector('[data-pe-sticky-value]');
    if (!input || !output) return;
    const min = Number(input.min) || 1;
    const step = Number(input.step) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value) || min;
    const next = Math.min(max, Math.max(min, current + direction * step));
    /** @type {any} */
    const selector = input.closest('quantity-selector-component');
    if (selector && typeof selector.setValue === 'function') selector.setValue(String(next));
    else input.value = String(next);
    selector?.updateButtonStates?.();
    output.textContent = String(next);
  }

  /* ---------------------------------------------------------------- shelf recommendations */

  async #loadRecommendations() {
    const shelf = /** @type {HTMLElement | null} */ (this.querySelector('[data-pe-shelf][data-recs-url]'));
    const url = shelf?.dataset.recsUrl;
    if (!shelf || !url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const list = doc.querySelector('[data-pe-shelf-list]');
      const current = shelf.querySelector('[data-pe-shelf-list]');
      if (!list || !current || !list.querySelector('.pe-shelf-item')) return;
      const fresh = /** @type {HTMLElement} */ (document.importNode(list, true));
      fresh.setAttribute('role', 'list');
      current.replaceWith(fresh);
      shelf.hidden = false;
    } catch {
      /* keep the server-rendered collection fallback */
    }
  }
}

if (!customElements.get('pe-product-info')) {
  customElements.define('pe-product-info', PeProductInfo);
}
